import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import { VerifiedUser, ErrorOutline, Block } from '@material-ui/icons';
import clsx from 'clsx';
import { IPve } from '@pvplevelup/core/dist/interfaces/Pve/IPve';
import { getPveRunVerification } from '@pvplevelup/core/dist/pve/PveRunVerification';

const VERIFIED = '#66bb6a';
const UNVERIFIED = '#ffb74d';
const INVALID = '#ff8a80';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles(() => ({
  badge: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 5,
    alignItems: 'center',
    height: 22,
    padding: '0 9px',
    borderRadius: 11,
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
    border: '1px solid transparent',
    cursor: 'default',
    '& svg': {
      fontSize: 13,
    },
  },
  verified: {
    color: VERIFIED,
    background: 'rgba(102, 187, 106, 0.12)',
    borderColor: 'rgba(102, 187, 106, 0.45)',
  },
  unverified: {
    color: UNVERIFIED,
    background: 'rgba(255, 183, 77, 0.1)',
    borderColor: 'rgba(255, 183, 77, 0.4)',
  },
  invalid: {
    color: INVALID,
    background: 'rgba(239, 83, 80, 0.12)',
    borderColor: 'rgba(239, 83, 80, 0.45)',
  },
  tip: {
    display: 'grid',
    gridGap: 3,
    padding: '2px 0',
    fontSize: '0.78rem',
  },
  tipTitle: {
    fontWeight: 800,
  },
}));

/**
 * Whether a finished run is verified (no ad-hoc players, cleared, hard mode met where it exists), with the reasons in
 * the tooltip when it isn't. Invalid runs (never on a leaderboard) are red. Nothing while the run is still going.
 */
export const VerifiedBadge: React.FC<{pve: IPve, className?: string}> = ({pve, className}) => {
  const classes = useStyles();
  const verification = getPveRunVerification(pve);
  if(!verification)
    return null;
  const {verified, invalid, unverifiedReasons} = verification;
  const title = verified
    ? <div className={classes.tip}><div className={classes.tipTitle}>Verified run</div><div>Everyone used the app, the run was cleared, and hard mode was met where the dungeon has one.</div></div>
    : <div className={classes.tip}>
        <div className={classes.tipTitle}>{invalid ? 'Unverified, and not on the leaderboards' : 'Unverified run'}</div>
        {unverifiedReasons.map((reason) => <div key={reason}>· {reason}</div>)}
      </div>;
  return (
    <Tooltip title={title}>
      <span className={clsx(classes.badge, className, verified ? classes.verified : invalid ? classes.invalid : classes.unverified)}>
        {verified ? <VerifiedUser /> : invalid ? <Block /> : <ErrorOutline />}
        {verified ? 'Verified' : 'Unverified'}
      </span>
    </Tooltip>
  );
}
